import type { SearchResultItem, ContentCardItem } from '@content-search/domain';
import { createContentCardItem } from '@content-search/utils';
import { Illustration, IllustrationLayout, IllustrationType } from '@core/components';
import { ContentType } from '@core/domain';
import { Card, Shimmer, Dropdown } from '@soteria/design-system-ve';

import { ContentCard } from './ContentCard';
import * as styles from './SearchResultsHelpers.css';

// Re-export createContentCardItem from utils (moved to avoid component-layer imports in hooks)
export { createContentCardItem };

// Limit options for the dropdown
export const LIMIT_OPTIONS = [
  { id: '25', value: '25', label: '25 per page' },
  { id: '50', value: '50', label: '50 per page' },
  { id: '100', value: '100', label: '100 per page' },
  { id: '200', value: '200', label: '200 per page' },
];

// Simplified component for rendering a single result item
export function ResultItem({
  contentCardItem,
  mediaServiceUrl,
  onShowContext,
  isSelected,
}: {
  contentCardItem: ContentCardItem;
  mediaServiceUrl?: string;
  onShowContext: (params: { subjectId: string; topicName: string; metadata: Record<string, unknown>; cardElement: HTMLElement }) => void;
  isSelected?: boolean;
}) {
  // Username content types don't have conversation context, so disable click-to-context
  const isUsername = contentCardItem.type === ContentType.USERNAME;

  // Handle card click to show context
  const handleCardClick = () => {
    // Find the card element by looking for the closest card container
    const cardElement = document.querySelector(`[data-subject-id="${contentCardItem.subjectId}"]`) as HTMLElement;

    if (cardElement) {
      onShowContext({
        subjectId: contentCardItem.subjectId,
        topicName: contentCardItem.topicName || 'Default',
        metadata: contentCardItem.metadata,
        cardElement,
      });
    }
  };

  return (
    <div className={styles.resultItem({ selected: isSelected })} data-subject-id={contentCardItem.subjectId}>
      <ContentCard item={contentCardItem} mediaServiceUrl={mediaServiceUrl} onClick={isUsername ? undefined : handleCardClick} />
    </div>
  );
}

// Separate component for rendering content cards
export function ResultsList({
  results,
  mediaServiceUrl,
  onShowContext,
  selectedSubjectId,
}: {
  results: SearchResultItem[];
  mediaServiceUrl?: string;
  onShowContext: (params: { subjectId: string; topicName: string; metadata: Record<string, unknown>; cardElement: HTMLElement }) => void;
  selectedSubjectId?: string;
}) {
  return (
    <div className={styles.resultsList}>
      {results.map((item, index) => {
        const contentCardItem = createContentCardItem(item, index);
        const key = `result-${contentCardItem.subjectId}`;
        const isSelected = selectedSubjectId === contentCardItem.subjectId;

        return (
          <ResultItem key={key} contentCardItem={contentCardItem} isSelected={isSelected} mediaServiceUrl={mediaServiceUrl} onShowContext={onShowContext} />
        );
      })}
    </div>
  );
}

// Component for rendering empty state
export function EmptyState({ hasSearched, isLoading }: { hasSearched: boolean; isLoading: boolean }) {
  if (isLoading) {
    return (
      <Card>
        <Shimmer className={styles.shimmerPlaceholder} />
      </Card>
    );
  }

  return (
    <Card>
      <Illustration
        description={
          hasSearched
            ? "We couldn't find any content matching your search criteria. Try adjusting your filters."
            : 'Use the filters above to search for content.'
        }
        layout={IllustrationLayout.HORIZONTAL}
        title={hasSearched ? 'No results found' : 'Start your search'}
        type={IllustrationType.EMPTY}
      />
    </Card>
  );
}

// Component for rendering the controls (limit dropdowns)
export function ResultsControls({ limit, isLoading, onLimitChange }: { limit: string; isLoading: boolean; onLimitChange: (value: string) => void }) {
  return (
    <div className={styles.resultsControls}>
      <Dropdown disabled={isLoading} id="limit" options={LIMIT_OPTIONS} selectedItem={limit} showCheckmark={false} onSelectionChange={onLimitChange} />
    </div>
  );
}
